import React, { useState, useEffect } from 'react';
import { useNavigate, Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NotificationIcon from '../components/admin/NotificationIcon';

// Custom SVG Icons
const MenuIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
  </svg>
);

const ExitToAppIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
  </svg>
);

const DashboardIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
  </svg>
);

const SettingsIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
  </svg>
);

const DescriptionIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
  </svg>
);

const InventoryIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
  </svg>
);

const drawerWidth = 260;

const AdminLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon className="w-6 h-6" />, path: '/admin/dashboard' },
    { text: 'Tickets', icon: <InventoryIcon className="w-6 h-6" />, path: '/admin/tickets' },
    { text: 'Reports', icon: <DescriptionIcon className="w-6 h-6" />, path: '/admin/reports' },
    { text: 'Settings', icon: <SettingsIcon className="w-6 h-6" />, path: '/admin/settings' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <div className="flex bg-gray-50 min-h-screen font-poppins">
      {/* App Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-blue-600 shadow-sm">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6">
          <div className="flex items-center">
            <button
              onClick={handleDrawerToggle}
              className="mr-2 p-2 text-white hover:bg-blue-700 rounded-lg sm:hidden"
            >
              <MenuIcon className="w-6 h-6" />
            </button>
            <Link
              to="/admin/dashboard"
              className="text-xl font-semibold text-white font-poppins no-underline hover:text-blue-100"
            >
              Ticket System
            </Link>
          </div>
          
          <div className="flex items-center space-x-2">
            <NotificationIcon />
            <button
              onClick={handleLogout}
              className="hidden sm:flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors font-poppins font-medium"
            >
              <ExitToAppIcon className="w-5 h-5" />
              Logout
            </button>
            <button
              onClick={handleLogout}
              className="sm:hidden p-2 text-white hover:bg-blue-700 rounded-lg"
            >
              <ExitToAppIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <div className={`fixed top-16 left-0 h-full bg-white border-r border-gray-200 font-poppins transition-transform duration-300 ${
        isMobile 
          ? (mobileOpen ? 'translate-x-0' : '-translate-x-full') 
          : 'translate-x-0'
      } w-64 z-40`}>
        {/* User Profile */}
        <div className="flex items-center px-6 py-4 border-b border-gray-100">
          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold font-poppins">
            {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
          </div>
          <div className="ml-3">
            <div className="text-sm font-semibold text-gray-900 font-poppins">
              {user?.name || 'Admin User'}
            </div>
            <div className="text-xs text-gray-600 font-poppins">
              {user?.role === 'ADMIN' ? 'Administrator' : user?.role === 'STAFF' ? 'ICT Coordinator' : user?.role || 'Administrator'}
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <div className="px-6 py-4">
          <div className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-4 font-poppins">
            MAIN MENU
          </div>
          <nav className="space-y-1">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.text}
                  to={item.path}
                  className={`flex items-center px-3 py-3 rounded-lg transition-all duration-200 font-poppins ${
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  <div className={`w-6 h-6 mr-3 ${
                    isActive ? 'text-white' : 'text-blue-600'
                  }`}>
                    {React.cloneElement(item.icon, { 
                      className: `w-6 h-6 ${isActive ? 'text-white' : 'text-blue-600'}` 
                    })}
                  </div>
                  <span className="text-sm font-medium">{item.text}</span>
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${
        isMobile ? 'ml-0' : 'ml-64'
      }`}>
        <div className="pt-16 p-6 sm:p-8 md:p-10 bg-gray-50 min-h-screen font-poppins">
          <Outlet />
        </div>
      </div>

      {/* Mobile Overlay */}
      {isMobile && mobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={handleDrawerToggle}
        />
      )}
    </div>
  );
};

export default AdminLayout;   